import { useState } from 'react';
import { formatMinutesShort } from '@cookbook/domain';
import { Link } from 'react-router-dom';
import type { Category, RecipeSummary } from '../api/types.ts';
import { AppIcon } from './AppIcon.tsx';
import { DifficultyBadge } from './DifficultyBadge.tsx';
import { FavoriteButton } from './FavoriteButton.tsx';
import { Stars } from './Stars.tsx';

/** Eigenschaften der Rezeptkarte. */
export interface RecipeCardProps {
  readonly recipe: RecipeSummary;
  readonly categories: readonly Category[];
  /** Liste statt Kachel. */
  readonly list?: boolean;
}

/** Eine Rezeptkarte für die Übersicht. */
export function RecipeCard({ recipe, categories, list = false }: RecipeCardProps) {
  const [favorite, setFavorite] = useState(recipe.isFavorite);

  const categoryNames = recipe.categoryIds
    .map((id) => categories.find((entry) => entry.id === id)?.name)
    .filter((name): name is string => name !== undefined)
    .slice(0, 2);

  return (
    <Link
      to={`/rezepte/${recipe.id}`}
      className={`group overflow-hidden rounded-card border border-border-subtle bg-surface-raised transition-shadow hover:shadow-md ${
        list ? 'flex items-stretch gap-4' : 'flex flex-col'
      }`}
    >
      {recipe.coverImageUrl === null ? (
        <span
          className={`flex shrink-0 items-center justify-center bg-accent-soft text-accent-strong ${
            list ? 'w-24' : 'h-32 w-full'
          }`}
        >
          <AppIcon name="bowl" size={list ? 28 : 40} />
        </span>
      ) : (
        <img
          src={recipe.coverImageUrl}
          alt=""
          loading="lazy"
          className={`shrink-0 object-cover ${list ? 'w-24' : 'h-40 w-full'}`}
        />
      )}

      <span className={`min-w-0 flex-1 ${list ? 'py-3 pr-3' : 'p-4'}`}>
        <span className="flex items-start justify-between gap-2">
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold group-hover:text-accent">{recipe.title}</span>
            {recipe.description === null ? null : (
              <span className="mt-0.5 block truncate text-sm text-content-muted">{recipe.description}</span>
            )}
          </span>
          <FavoriteButton
            recipeId={recipe.id}
            title={recipe.title}
            favorite={favorite}
            onChange={setFavorite}
          />
        </span>

        <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-content-muted">
          <Stars value={recipe.ratingAverage} count={recipe.ratingCount} size={14} />
          <span className="inline-flex items-center gap-1">
            <AppIcon name="clock" size={14} />
            {formatMinutesShort(recipe.totalMinutes)}
          </span>
          <span className="inline-flex items-center gap-1">
            <AppIcon name="flame" size={14} />
            {formatMinutesShort(recipe.activeMinutes)} aktiv
          </span>
          {recipe.imageCount === 0 ? null : (
            <span className="inline-flex items-center gap-1">
              <AppIcon name="tag" size={14} />
              {recipe.imageCount}
            </span>
          )}
        </span>

        <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <DifficultyBadge difficulty={recipe.difficulty} />
          {categoryNames.map((name) => (
            <span key={name} className="rounded-full bg-surface-sunken px-2 py-0.5 text-[11px]">
              {name}
            </span>
          ))}
          {recipe.tagNames.slice(0, 3).map((name) => (
            <span
              key={name}
              className="rounded-full bg-accent-soft px-2 py-0.5 text-[11px] text-accent-strong"
            >
              {name}
            </span>
          ))}
        </span>
      </span>
    </Link>
  );
}
